/**
 * Visual Props Resolver — Extracts and normalizes props for each visual type.
 *
 * Extracted from VisualAidFactory.tsx to keep the factory thin (<50 lines).
 * Each visual type has specific prop extraction logic that maps raw seed `data`
 * to the component's expected props interface.
 */

import { resolveFractionVisualType } from '../fractionVisualTypeResolver'

type DataRecord = Record<string, unknown>

// ─── Data extraction helpers ──────────────────────────────────────────────────

function getString(data: DataRecord, key: string): string | undefined {
  const v = data[key]
  return typeof v === 'string' ? v : undefined
}

function getNumber(data: DataRecord, key: string): number | undefined {
  const v = data[key]
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined
}

function getBoolean(data: DataRecord, key: string): boolean | undefined {
  const v = data[key]
  return typeof v === 'boolean' ? v : undefined
}

function getRecord(
  data: DataRecord,
  key: string,
): Record<string, unknown> | undefined {
  const v = data[key]
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : undefined
}

function getArray(data: DataRecord, key: string): unknown[] | undefined {
  const v = data[key]
  return Array.isArray(v) ? v : undefined
}

// ─── Type alias normalization ─────────────────────────────────────────────────

/**
 * Normalize visual type aliases to canonical names.
 */
export function normalizeVisualType(type: string): string {
  if (type === 'numberLine') return 'numberline'
  if (type === 'moneyCoins') return 'money'
  if (type === 'decimalGrid') return 'decimalgrid'
  if (type === 'analog_clock') return 'clock'
  if (type === 'digital_clock') return 'digitalClock'
  if (type === 'fraction_bar' || type === 'fraction_circle') return 'fraction'
  return type
}

// ─── Per-type prop resolvers ──────────────────────────────────────────────────

type PropResolver = (
  data: DataRecord,
  mode: string,
) => Record<string, unknown> | null

const resolvers: Record<string, PropResolver> = {
  stairs(data, mode) {
    const stairsType = getString(data, 'type')
    if (!stairsType) return null
    return {
      type: stairsType,
      from: getString(data, 'from'),
      to: getString(data, 'to'),
      value: getNumber(data, 'value') ?? 0,
      highlight: getBoolean(data, 'highlight') ?? false,
      showAnimation: getBoolean(data, 'showAnimation') ?? false,
      mode,
    }
  },

  circle(data, mode) {
    return {
      radius: getNumber(data, 'radius'),
      diameter: getNumber(data, 'diameter'),
      circumference: getNumber(data, 'circumference'),
      area: getNumber(data, 'area'),
      label: getString(data, 'label'),
      showFormula: getBoolean(data, 'showFormula') ?? false,
      highlightRadius: getBoolean(data, 'highlightRadius') ?? false,
      highlightDiameter: getBoolean(data, 'highlightDiameter') ?? false,
      sectorAngle: getNumber(data, 'sectorAngle'),
      highlightSector: getBoolean(data, 'highlightSector') ?? false,
      mode,
    }
  },

  fraction(data, mode) {
    const denominator = getNumber(data, 'denominator') ?? 4
    if (denominator === 0) return null
    const value = getNumber(data, 'value') ?? 0
    const numerator =
      getNumber(data, 'numerator') ?? Math.round(value * denominator)
    const rawVisualType = getString(data, 'visualType')
    const explicitType =
      rawVisualType === 'pizza' || rawVisualType === 'bar'
        ? (rawVisualType as 'pizza' | 'bar')
        : undefined
    const questionText = getString(data, 'questionText') ?? ''
    const options = getArray(data, 'options') as string[] | undefined
    const visualType = resolveFractionVisualType(
      questionText,
      options,
      explicitType,
    )
    return {
      numerator,
      denominator,
      label: getString(data, 'label'),
      type: visualType,
      showEaten: getBoolean(data, 'showEaten') ?? false,
      mode,
      fractions: getArray(data, 'fractions') as
        | Array<{
            numerator: number
            denominator: number
            label?: string
            visualType?: string
          }>
        | undefined,
      operator: (getString(data, 'operator') as '+' | '-' | '×' | '÷') ?? '+',
    }
  },

  geo(data, mode) {
    const width = getNumber(data, 'width')
    const height = getNumber(data, 'height')
    if (!width || !height) return null
    return {
      width,
      height,
      depth: getNumber(data, 'depth'),
      label: getString(data, 'label'),
      showGrid: getBoolean(data, 'showGrid'),
      unit: getString(data, 'unit'),
      mode,
    }
  },

  decimalgrid(data, mode) {
    return {
      width: getNumber(data, 'cols') ?? getNumber(data, 'width') ?? 10,
      height: getNumber(data, 'rows') ?? getNumber(data, 'height') ?? 10,
      highlighted:
        getNumber(data, 'highlighted') ?? getNumber(data, 'value') ?? 0,
      label: getString(data, 'label'),
      mode,
    }
  },

  numberline(data, mode) {
    return { ...(data as Record<string, unknown>), mode }
  },

  ratio(data, mode) {
    const headers = getArray(data, 'headers')
    const rows = getArray(data, 'rows')
    if (!headers || !rows) return null
    return { ...(data as Record<string, unknown>), mode }
  },

  rule(data, mode) {
    const title = getString(data, 'title')
    const content = getString(data, 'content')
    if (!title || !content) return null
    return { ...(data as Record<string, unknown>), mode }
  },

  money(data, mode) {
    const amount = getNumber(data, 'amount')
    if (amount === undefined) return null
    return {
      amount,
      breakdown: getRecord(data, 'breakdown'),
      showCoins: data['showCoins'] !== false,
      showBills: data['showBills'] !== false,
      interactive: getBoolean(data, 'interactive') ?? false,
      label: getString(data, 'label'),
      mode,
    }
  },

  column(data, mode) {
    const operation = getString(data, 'operation')
    if (!operation) return null
    const operand1 = data['operand1'] ?? getArray(data, 'numbers')?.[0]
    const operand2 = data['operand2'] ?? getArray(data, 'numbers')?.[1]
    if (operand1 === undefined || operand2 === undefined) return null
    return {
      operation,
      operand1,
      operand2,
      showSteps: data['showSteps'] !== false,
      currentStep: getNumber(data, 'currentStep') ?? 0,
      animated: data['animated'] !== false,
      onStepChange: data['onStepChange'],
      mode,
    }
  },

  clock(data, mode) {
    return {
      hours: getNumber(data, 'hours') ?? getNumber(data, 'hour') ?? 12,
      minutes: getNumber(data, 'minutes') ?? getNumber(data, 'minute') ?? 0,
      showSeconds: false,
      label: getString(data, 'label'),
      mode,
    }
  },

  digitalClock(data, mode) {
    return {
      hours: getNumber(data, 'hours') ?? getNumber(data, 'hour') ?? 12,
      minutes: getNumber(data, 'minutes') ?? getNumber(data, 'minute') ?? 0,
      seconds: getNumber(data, 'seconds'),
      showSeconds: getBoolean(data, 'showSeconds') ?? false,
      format: (getString(data, 'format') as '12h' | '24h') ?? '24h',
      label: getString(data, 'label'),
      mode,
    }
  },

  kofschipMnemonic(data, mode) {
    return {
      verb: getString(data, 'verb') ?? '',
      stem: getString(data, 'stem') ?? '',
      lastLetter: getString(data, 'lastLetter') ?? '',
      useTE: getBoolean(data, 'useTE') ?? false,
      label: getString(data, 'label'),
      mode,
    }
  },

  syllableBlocks(data, mode) {
    const syllables = getArray(data, 'syllables') as string[] | undefined
    const types = getArray(data, 'types') as ('open' | 'closed')[] | undefined
    if (!syllables || !types) return null
    return {
      word: getString(data, 'word') ?? '',
      syllables,
      types,
      highlight: getNumber(data, 'highlight'),
      label: getString(data, 'label'),
      mode,
    }
  },

  verbTable(data, mode) {
    const persons = getArray(data, 'persons') as string[] | undefined
    const forms = getArray(data, 'forms') as string[] | undefined
    if (!persons || !forms) return null
    return {
      verb: getString(data, 'verb') ?? '',
      tense:
        (getString(data, 'tense') as 'present' | 'past' | 'perfect') ??
        'present',
      persons,
      forms,
      highlight: getString(data, 'highlight'),
      label: getString(data, 'label'),
      mode,
    }
  },

  coordinateGrid(data, mode) {
    return {
      width: getNumber(data, 'width') ?? 10,
      height: getNumber(data, 'height') ?? 10,
      points: getArray(data, 'points') as
        | Array<{
            x: number
            y: number
            label: string
            color?: string
          }>
        | undefined,
      label: getString(data, 'label'),
      showGrid: getBoolean(data, 'showGrid') ?? true,
      mode,
    }
  },

  box3d(data, mode) {
    return {
      length: getNumber(data, 'length') ?? getNumber(data, 'width') ?? 5,
      width: getNumber(data, 'depth') ?? getNumber(data, 'width') ?? 5,
      height: getNumber(data, 'height') ?? 5,
      unit: getString(data, 'unit') ?? 'cm',
      label: getString(data, 'label'),
      showGrid: getBoolean(data, 'showGrid') ?? true,
      mode,
    }
  },

  compositeShape(data, mode) {
    const rectangles = getArray(data, 'rectangles') as
      | Array<{
          x: number
          y: number
          width: number
          height: number
          label?: string
        }>
      | undefined
    if (!rectangles || rectangles.length === 0) return null
    return {
      rectangles,
      unit: getString(data, 'unit') ?? 'cm',
      label: getString(data, 'label'),
      showGrid: getBoolean(data, 'showGrid') ?? true,
      showDimensions: getBoolean(data, 'showDimensions') ?? true,
      mode,
    }
  },

  readingPassage(data, mode) {
    const passage = getString(data, 'passage')
    if (!passage) return null
    return {
      passage,
      instruction: getString(data, 'instruction'),
      source: getString(data, 'source'),
      label: getString(data, 'label'),
      mode,
    }
  },

  geometricShape(data, mode) {
    const shape = getString(data, 'shape') as
      | 'rectangle'
      | 'triangle'
      | 'parallelogram'
      | 'trapezoid'
      | undefined
    const base = getNumber(data, 'base')
    const height = getNumber(data, 'height')
    if (!shape || !base || !height) return null
    return {
      shape,
      base,
      height,
      sideLength: getNumber(data, 'sideLength'),
      topBase: getNumber(data, 'topBase'),
      unit: getString(data, 'unit') ?? 'cm',
      label: getString(data, 'label'),
      showGrid: getBoolean(data, 'showGrid') ?? true,
      showDimensions: getBoolean(data, 'showDimensions') ?? true,
      hideHeight: getBoolean(data, 'hideHeight') ?? false,
      mode,
    }
  },

  barChart(data, mode) {
    const chartData = getArray(data, 'data') as
      | Array<{ label: string; value: number }>
      | undefined
    if (!chartData || chartData.length === 0) return null
    return {
      data: chartData,
      xAxisLabel: getString(data, 'xAxisLabel'),
      yAxisLabel: getString(data, 'yAxisLabel'),
      barColor: getString(data, 'barColor'),
      hideValues: getBoolean(data, 'hideValues') ?? true,
      mode,
    }
  },

  lineChart(data, mode) {
    const chartData = getArray(data, 'data') as
      | Array<{ label: string; value: number }>
      | undefined
    if (!chartData || chartData.length === 0) return null
    return {
      data: chartData,
      xAxisLabel: getString(data, 'xAxisLabel'),
      yAxisLabel: getString(data, 'yAxisLabel'),
      lineColor: getString(data, 'lineColor'),
      hideValues: getBoolean(data, 'hideValues') ?? true,
      mode,
    }
  },

  calendar(data, mode) {
    const year = getNumber(data, 'year')
    const month = getNumber(data, 'month')
    if (!year || !month) return null
    return {
      year,
      month,
      highlightDates: getArray(data, 'highlightDates') as number[] | undefined,
      todayDate: getNumber(data, 'todayDate'),
      targetDate: getNumber(data, 'targetDate'),
      secondMonth: getNumber(data, 'secondMonth'),
      secondMonthYear: getNumber(data, 'secondMonthYear'),
      secondMonthHighlight: getArray(data, 'secondMonthHighlight') as
        | number[]
        | undefined,
      secondMonthTarget: getNumber(data, 'secondMonthTarget'),
      label: getString(data, 'label'),
      mode,
    }
  },

  thermometer(data, mode) {
    const currentTemp = getNumber(data, 'currentTemp')
    if (currentTemp === undefined) return null
    return {
      minTemp: getNumber(data, 'minTemp') ?? -20,
      maxTemp: getNumber(data, 'maxTemp') ?? 50,
      currentTemp,
      targetTemp: getNumber(data, 'targetTemp'),
      unit: getString(data, 'unit') ?? 'C',
      label: getString(data, 'label'),
      mode,
    }
  },

  ruler(data, mode) {
    return {
      length: getNumber(data, 'length') ?? 15,
      objectStart: getNumber(data, 'objectStart'),
      objectEnd: getNumber(data, 'objectEnd'),
      unit: getString(data, 'unit') ?? 'cm',
      label: getString(data, 'label'),
      hideValue: getBoolean(data, 'hideValue') ?? false,
      mode,
    }
  },

  measuringCup(data, mode) {
    const maxVolume = getNumber(data, 'maxVolume')
    const currentVolume = getNumber(data, 'currentVolume')
    if (!maxVolume || currentVolume === undefined) return null
    return {
      maxVolume,
      currentVolume,
      targetVolume: getNumber(data, 'targetVolume'),
      unit: getString(data, 'unit') ?? 'ml',
      label: getString(data, 'label'),
      hideValue: getBoolean(data, 'hideValue') ?? false,
      mode,
    }
  },

  weighScale(data, mode) {
    const leftWeight = getNumber(data, 'leftWeight')
    const rightWeight = getNumber(data, 'rightWeight')
    if (leftWeight === undefined || rightWeight === undefined) return null
    return {
      leftWeight,
      rightWeight,
      leftLabel: getString(data, 'leftLabel'),
      rightLabel: getString(data, 'rightLabel'),
      unit: getString(data, 'unit') ?? 'kg',
      label: getString(data, 'label'),
      showQuestion: getBoolean(data, 'showQuestion') ?? false,
      mode,
    }
  },

  equalSharing(data, mode) {
    const total = getNumber(data, 'total')
    const parts = getNumber(data, 'parts')
    const perPart = getNumber(data, 'perPart')
    if (total === undefined || parts === undefined || perPart === undefined)
      return null
    return {
      total,
      parts,
      perPart,
      unit: getString(data, 'unit'),
      label: getString(data, 'label'),
      mode,
    }
  },
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Resolve props for a visual component given its type and raw data.
 * Returns null if required data is missing (component should not render).
 */
export function resolveVisualProps(
  type: string,
  data: Record<string, unknown>,
  mode: string,
): Record<string, unknown> | null {
  const resolver = resolvers[type]
  if (!resolver) return null
  return resolver(data, mode)
}
